import type { FavoriteTargetType } from "@compass/shared/schema/favorite";
import { TrendingUp } from "lucide-react";
import { buildFavoriteTarget } from "components/elements/ContentCard";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { TrendingLink } from "components/elements/TrendingLink";
import type { RouterOutputs } from "lib/trpc";
import { trpc } from "lib/trpc";

type TrendingItem = RouterOutputs["favorites"]["getTrending"][number];

function getTrendingTitle(item: TrendingItem): string {
    switch (item.type) {
        case "vendor":
            return item.data.name;
        default:
            return item.data.title ?? "Untitled";
    }
}

interface TrendingRowProps {
    filterType?: FavoriteTargetType;
    title?: string;
    limit?: number;
}

export const TrendingRow = ({ filterType, title = "Trending", limit = 6 }: TrendingRowProps) => {
    const { data, isLoading } = trpc.favorites.getTrending.useQuery({
        limit,
        sinceDays: 7,
        filterType,
    });

    if (isLoading || !data || data.length === 0) return null;

    return (
        <div className="mb-6">
            <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-orange-500" />
                <h3 className="font-sans text-[18px] font-semibold text-[#424242]">{title}</h3>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
                {data.map((item) => {
                    const id = item.data.id;
                    return (
                        <TrendingLink
                            key={`${item.type}:${id}`}
                            type={item.type}
                            id={id}
                            className="no-underline"
                        >
                            <div className="flex min-w-[200px] max-w-[240px] shrink-0 items-start gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                <div className="min-w-0 flex-1">
                                    <p className="line-clamp-2 text-sm font-medium text-gray-900">
                                        {getTrendingTitle(item)}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-400">
                                        Score: {item.score}
                                    </p>
                                </div>
                                <FavoriteButton
                                    target={buildFavoriteTarget(item.type, id)}
                                    size={14}
                                />
                            </div>
                        </TrendingLink>
                    );
                })}
            </div>
        </div>
    );
};
